import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { SearchField as Search } from "@onelogin/react-components";

import getCaretCoordinates from "../utils/caretCoordinates";
import { NARROW_VIEW_LIMIT } from "../constants/Env";
import * as keyCodes from "../constants/keyCodes";

import "./SearchField.css";

const Caret = styled.div`
  content: "|";
  opacity: 1;
  font-weight: 100;
  animation: blink-caret 1s infinite;
  height: 21px;
  position: absolute;
  top: 38px;
  left: ${props => props.left}px;
  width: 1px;
  overflow: hidden;

  @keyframes blink-caret {
    from,
    to {
      background-color: transparent;
    }
    50% {
      background-color: #000;
    }
  }

  @media screen and (max-width: ${NARROW_VIEW_LIMIT}px) {
    top: 28px;
  }
`;

Caret.displayName = "Caret";

export class SearchField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      caretLeft: 0,
      showCaret: !props.shouldHaveFocus
    };
  }

  componentDidMount() {
    this.setCaretState();
    this.handleFocus(this.props.shouldHaveFocus);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searchTerm !== this.props.searchTerm) {
      //mainly due to ESC which clears the searchTerm; onChange is not called in this case
      this.setCaretState();
    }

    if (prevProps.shouldHaveFocus !== this.props.shouldHaveFocus) {
      this.handleFocus(this.props.shouldHaveFocus);
    }
  }

  keyupHandler = event => {
    if (event.keyCode === keyCodes.ENTER_KEY_CODE) {
      this.props.onSearchEnter(event);
    }
  };

  keydownHandler = event => {
    if (event.keyCode === keyCodes.ESCAPE_KEY_CODE) {
      this.onSearchTermRemove();
    } else if (
      this.props.shouldHaveFocus &&
      event.keyCode === keyCodes.ARROW_DOWN_KEY_CODE
    ) {
      event.preventDefault();
      this.props.leaveSearchFieldIfPossible();
    }
  };

  onBlur = event => {
    this.setState({
      showCaret: true
    });

    this.setCaretState();
    this.props.onBlur(event);
  };

  setCaretState = () => {
    if (this.props.isTouchDevice) {
      return;
    }

    const caretPosition = getCaretCoordinates(
      this.input,
      this.input.selectionStart
    );

    this.setState({
      caretLeft: caretPosition.left
    });
  };

  onSearchTermRemove = () => {
    this.props.onChange("");
  };

  handleFocus = focus => {
    if (this.input && focus) {
      this.props.focusEl(this.input);
    }
  };

  onFocus = event => {
    if (event.target.selectionStart) {
      const index = this.input.value.length;
      this.input.setSelectionRange(index, index);
    }

    this.setState({
      showCaret: false
    });

    this.props.onFocus();
  };

  assignRef = input => {
    this.input = input;
  };

  onChange = event => {
    this.props.onChange(event.target.value);
  };

  render() {
    const { searchTerm, isTouchDevice, onClick, placeholder } = this.props;

    return (
      <div className="search-container" role="search">
        <Search
          id="search-input"
          data-testid="search-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onClick={onClick}
          onRemove={this.onSearchTermRemove}
          onBlur={this.onBlur}
          onKeyDown={
            !this.props.isTouchDevice ? this.keydownHandler : undefined
          }
          onKeyUp={!this.props.isTouchDevice ? this.keyupHandler : undefined}
          inputRef={this.assignRef}
          size="big"
          type="text"
          aria-label={placeholder}
        />
        {this.state.showCaret && !isTouchDevice ? (
          <Caret left={this.state.caretLeft} data-testid="search-field-caret" />
        ) : null}
      </div>
    );
  }
}

SearchField.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  focusEl: PropTypes.func.isRequired,
  shouldHaveFocus: PropTypes.bool.isRequired,
  placeholder: PropTypes.string.isRequired,
  isTouchDevice: PropTypes.bool.isRequired,
  leaveSearchFieldIfPossible: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onSearchEnter: PropTypes.func.isRequired
};

export default SearchField;
